import { type ComponentProps, type ReactNode } from 'react'

import { ButtonDefault, ButtonRoot } from '@/components/common/Button'
import { FloatingPopover } from '@/components/common/Floating'
import { LinkOutbound } from '@/components/common/Link'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { CARDART, type DistributiveOmit, RELATED_URLS } from '@/defs/misc'

import { Splash } from '../../Splash'

import styles from './SupportUsPopover.module.scss'

type LinkProps = DistributiveOmit<ComponentProps<typeof LinkOutbound>, 'children' | 'href'>

type Props = {
  children?: ReactNode
  className?: string
  feature?: 'default' | 'rare'
  message?: string
  mode?: 'popover' | 'content'
} & LinkProps

type PatreonLinkProps = {
  children: ReactNode
} & LinkProps

const PatreonLink = ({ children, ...props }: PatreonLinkProps) => (
  <LinkOutbound href={RELATED_URLS.patreon} {...props}>
    {children}
  </LinkOutbound>
)

const CONFIG = {
  default: 'Please consider supporting us on Patreon to remove ads for only $2/month.',
  rare: 'Become a Rare Patron for $5/month to unlock this feature.',
}

export const SupportUsPopover = ({
  'aria-label': ariaLabel,
  children,
  className,
  feature = 'default',
  message,
  mode = 'popover',
  ...link
}: Props) => {
  const tooltipElement = (
    <div className={styles.tooltip}>
      <Splash align='right' backgroundImages={[CARDART.MANIFOLD_KEY]} subtle />
      <div className={styles.inner}>
        <h2>
          <PatreonLink {...link}>
            <IconWithLabel iconBefore='patreon'>Support us on Patreon</IconWithLabel>
          </PatreonLink>
        </h2>
        <p>
          <PatreonLink {...link}>{message || CONFIG[feature] || ''}</PatreonLink>
        </p>
        <div>
          <PatreonLink {...link}>
            <ButtonDefault as='div' colorScheme='patreon' size='sm'>
              Go to Patreon.com
            </ButtonDefault>
          </PatreonLink>
        </div>
      </div>
    </div>
  )

  if (mode === 'content') {
    return <div className={styles.container}>{tooltipElement}</div>
  }

  if (!children) return null

  return (
    <FloatingPopover borderColor='light' content={tooltipElement} padding='none'>
      <ButtonRoot aria-label={ariaLabel} className={className} title={link.title}>
        {children}
      </ButtonRoot>
    </FloatingPopover>
  )
}
